<template>
  <div class="step-question-template medical-step-question">
    <div class="step-questions-state">
      <div class="heading">
        <h1 class="step-questions-state-title">Which state do you live in?</h1>
        <p class="step-questions-state-description">
          We ship to most US states. Let’s just check we can ship to you too.
        </p>
      </div>
      <div class="functionality">
        <select
          v-model="selectedStateKey"
          class="step-questions-select"
          autocomplete="off"
          aria-label="Select the state you live in"
        >
          <option :value="null" selected>Select...</option>
          <option :value="state.key" v-for="state in states" :key="state.key">{{ state.name }}</option>
        </select>
        <div class="step-questions-state-news-box" v-if="hasSelectedState">
          <template v-if="canShip">
            <ThumbIcon name="thumbs-up" />
            <h2 class="news-box-title">Good news!</h2>
            <p class="news-box-description" v-html="selectedStateMessage" />
          </template>
          <template v-else-if="selectedState">
            <ThumbIcon name="thumbs-down" />
            <h2 class="news-box-title">Sorry, we can't ship to you</h2>
            <p class="news-box-description">
              {{ selectedStateMessage }}
            </p>
          </template>
        </div>
        <NextQuestionButton v-if="canShip" @click="emitProceed" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import ThumbIcon from "./ThumbIcon";
import NextQuestionButton from "@/Consultation/components/NextQuestionButton.vue";
import { amplitudeEvent } from "@/MXPixel/MedexpressAmplitude";

enum StateAvailability {
  AVAILABLE = "AVAILABLE",
  NOT_SUPPORTED = "NOT_SUPPORTED",
  NOT_PERMITTED_FOR_PRODUCT = "NOT_PERMITTED_FOR_PRODUCT",
  RESTRICTED_FOR_PRODUCT = "RESTRICTED_FOR_PRODUCT",
}

export interface State {
  name: string;
  /**
   * Two-letter abbreviation for the state
   */
  key: string;
  availability: StateAvailability;
  requiresAVC: boolean;
}

interface StateQuestionData {
  selectedStateKey: string | null;
}

export default Vue.extend({
  components: { NextQuestionButton, ThumbIcon },
  props: {
    states: { type: Array as PropType<State[]>, required: true },
    initialSelectedStateKey: { type: String, required: false },
  },
  mounted() {
    amplitudeEvent("View State Question");
  },
  data(): StateQuestionData {
    return {
      selectedStateKey: this.initialSelectedStateKey ?? null,
    };
  },
  methods: {
    emitProceed() {
      amplitudeEvent("Proceed State Question", {
        stateName: this.selectedState?.name,
        requiresAVC: this.selectedState?.requiresAVC,
      });

      window.sessionStorage.setItem("consultationStateQuestionSelection", this.selectedStateKey);

      this.$emit("next", this.selectedStateKey);
    },
  },
  computed: {
    hasSelectedState(): boolean {
      return !!this.selectedState;
    },
    selectedState(): State | undefined {
      if (!this.selectedStateKey) {
        return undefined;
      }
      return this.states.find((state) => state.key === this.selectedStateKey);
    },
    selectedStateMessage() {
      switch (this.selectedState?.availability) {
        case StateAvailability.AVAILABLE:
          return this.selectedState.requiresAVC
            ? `We ship to ${this.selectedState.name}. Please note, you will need to <strong>complete an online
               consultation</strong> with one of our clinicians after purchase in order to receive your medicine.`
            : `We ship to ${this.selectedState.name}. Let’s continue with your consultation.`;
        case StateAvailability.RESTRICTED_FOR_PRODUCT:
          return this.selectedState.requiresAVC
            ? `We are not able to sell or ship some products to your state, but treatment options are available.
               Please note, you will need to <strong>complete an online consultation</strong> with one of our clinicians
               after purchase in order to receive your medicine.`
            : `We are not able to sell or ship some products to your state, but treatment options are available. Please
               continue to see if you are eligible.`;
        case StateAvailability.NOT_SUPPORTED:
          return `We’re working on it, but we are not able to ship to ${this.selectedState.name} yet.`;
        case StateAvailability.NOT_PERMITTED_FOR_PRODUCT:
          return `We’re working on it, but we are not able to sell or ship this product to ${this.selectedState.name} yet.`;
        default:
          return null;
      }
    },
    canShip(): boolean {
      const availability: StateAvailability = this.selectedState?.availability;
      return availability === StateAvailability.AVAILABLE || availability === StateAvailability.RESTRICTED_FOR_PRODUCT;
    },
  },
  watch: {
    selectedState(newValue: State | undefined) {
      if (newValue) {
        amplitudeEvent("Select State", {
          stateName: newValue.name,
          isAvailable:
            newValue.availability === StateAvailability.AVAILABLE ||
            newValue.availability === StateAvailability.RESTRICTED_FOR_PRODUCT,
          requiresAVC: newValue.requiresAVC,
        });
      }
    },
  },
});
</script>
